<template>
  <div class='institutional-qnr-results-sample-ips view is-flex gap-2 py-6'>
    <QnrSectionNav :sections='sections'/>

    <div class='is-flex-col main ml-6'>
      <header class='mb-6'>
        <h1 class='title is-2'>Sample IPS Responsible Investment Statement</h1>
        <p class='subtitle is-6 has-text-grey'>Based on the responses, here is a generated text sample for the IPS Responsible Investment Statement</p>
      </header>

      <SampleIPS
        :group='group'
        :mission='mission'
        :goals='goalsWithText'
        :metrics='filteredMetrics'
        :strategies='strategies'
        :allocations='allocations'
        :includeHighImpact='includeHighImpact'
      />

      <!-- Turned off temporarily until we have time to fix -->
      <!--
      <div class='is-flex-col main'>
        <header class='mb-6'>
          <h1 class='title is-2'>Sample Portfolio Proposal</h1>
          <p class='subtitle is-6 has-text-grey'>Incorporate the common values into a sample portfolio proposal</p>
          <a href="/advisor-dashboard/reports/">Edit proposal</a>
        </header>

        <iframe scrolling="no"
          src="/institutional/my-sample-values-aligned-portfolio/?report_type=base&bgcolor=%23ffffff&dollarsOrPercentages=dollars&m=87%2C76%2C97%2C56%2C54%2C86%2C68%2C104%2C100%2C63%2C78&screens=89%2C15%2C43%2C23%2C98%2C30&metaphor_section=1&is_iframe=1"
          class="preview"
          style="transform: scale(0.925); height: 13867px; width: 1000px; left: -37.5px; top: -520.0125px; visibility: visible; position: relative;">
        </iframe>
      </div>
      -->

      <footer class='is-flex justify-between mt-6'>
        <router-link :to='prev'>
          <button class='button is-primary'>Previous</button>
        </router-link>
      </footer>
    </div>
  </div>
</template>


<script>
import QnrSectionNav from '../../components/institutional_questionnaire/QnrSectionNav.vue';
import SampleIPS from '../../components/institutional_questionnaire/SampleIPS.vue';
import { getMetrics } from '@frontend/api/api.js';


export default {
  name: 'institutional-qnr-results-sample-ips',
  components: { QnrSectionNav, SampleIPS },
  props: ['clientGroupID', 'sections'],
  data() {
    return {
      clientGroups: null,
      metricHash: {},
      assetClasses: {
        public: 'Public Equity Assets',
        fixed: 'Fixed Income Assets',
        private: 'Private Assets',
        cash: 'Cash',
      },
    };
  },
  beforeCreate() {},
  created() {
    this.getClientGroups();
    this.loadMetrics();
  },
  updated() {},
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
    last() {
      return this.sections.length;
    },
    prev() {
      return this.url(this.last);
    },
    mission() {
      const adminName = 'Kimberley Walker'; // TODO: This should be dynamic

      const res = this.getResponses('Understanding the Organization', 'mission');
      if (!res || res.length === 0) return null;

      const admin = res.find(r => r.name === adminName);
      if (admin) return admin.response;
      return res[0].response; // Return first response if admin not found
    },
    goals() {
      const res = this.getResponses('Your Organization\'s Goals', 'purpose');
      if (!res) return null;

      const top = [{}, {}, {}];

      res.forEach(r => {
        for (let i = 0; i < 3; i++) {
          const goal = r.response[i];
          if (!goal) continue;
          const name = typeof goal === 'string' ? goal : goal.name;
          if (name) top[i][name] = goal;
        }
      });

      // TODO: Add more from 2nd and 3rd choices if not enough in 1st.
      const goals = top[0];
      return Object.keys(goals).map(name => goals[name]);
    },
    goalsAsObjects() {
      if (!this.goals) return [];
      return this.goals.map(g => {
        if (typeof g === 'string') return { name: g };
        return { name: g.name ? g.name : g.text };
      });
    },
    goalTexts() {
      const q = this.findQuestion('Your Organization\'s Goals', 'purpose');
      if (!q) return {};

      const texts = {};

      q.choices.forEach(c => {
        const ips = c.ips ? c.ips : c.ips_text;
        texts[c.text] = ips;
      });
      return texts;
    },
    goalsWithText() {
      return this.goalsAsObjects.map(g => {
        const name = g.name;
        return { name, ips: this.goalTexts[name] };
      });
    },
    strategies() {
      const res = this.getResponses('Responsible Investing Strategies', 'philosophy');
      if (!res) return null;

      const counts = {};
      res.forEach(r => {
        r.response.forEach(strategy => {
          const s = strategy;
          counts[s] = counts[s] ? counts[s] + 1 : 1;
        });
      });

      const scored = Object.keys(counts).map(s => {
        return { name: s, pct: (counts[s] / res.length) * 100 };
      });
      scored.sort((a, b) => b.pct - a.pct);

      const top = scored.filter(s => s.pct > 10); // TODO: Set at 10 for testing; change to 50 for release
      if (top.length === 0) return scored.slice(0, 1);

      return top.slice(0, 3);
    },
    allocationResponses() {
      const section = this.findSection('Miscellaneous');

      return ['public', 'fixed', 'private', 'cash'].map(type => {
        const slug = 'guided_portions_' + type;
        const q = this.findQuestion(section, slug);
        return { type, responses: q ? q.responses : [] };
      });
    },
    allocationCounts() {
      return this.allocationResponses.map(r => {
        let count = 0;
        if (!r.responses) return { type: r.type, count: 0 };

        r.responses.forEach(r => {
          if (r.response && r.response !== 'None') count++;
        });
        return { type: r.type, count };
      });
    },
    // TODO: Need to calc allocationScores with count / #prospects
    allocations() {
      const filtered = this.allocationCounts.filter(a => {
        return a.count > 0;
      });

      return filtered.map(a => {
        const type = a.type;
        return { type, name: this.assetClasses[type] };
      });
    },
    includeHighImpact() {
      const responses = this.getResponses('Miscellaneous', 'high_impact');
      // Include by default if no responses
      if (!responses || responses.length === 0) return true;

      // Include if there are one more "Yes" responses
      return responses.map(r => r.response.toLowerCase()).some(r => r.startsWith('yes'));
    },
    group() {
      const id = this.clientGroupID;
      if (!id || !this.clientGroups) return {};

      return this.clientGroups.find(g => g.id === parseInt(id));
    },
    prospects() { 
      if (!this.group) return [];
      return this.group ? this.group.prospects : [];
    },
    screenSets() {
      if (!this.prospects) return [];
      const sets = this.prospects.map(p => p.screen_sets);
      return sets.filter(s => s.length > 0);
    },
    rawMetrics() {
      const metrics = [];

      this.screenSets.forEach(prospect => {
        prospect.forEach(set => {
          metrics.push(set.metrics);
        });
      });
      return metrics.flat();
    },
    metricCounts() {
      const counts = {};

      this.rawMetrics.forEach(m => {
        const id = m.metric_description;
        counts[id] = counts[id] ? counts[id] + 1 : 1;
      });
      return counts;
    },
    metricsWithInfo() {
      const metrics = Object.keys(this.metricCounts).map(id => {
        const count = this.metricCounts[id];
        return {
          count,
          pct: count / this.prospects.length,
          ...this.metricHash[id],
        };
      });

      return metrics.sort((a, b) => b.pct - a.pct);
    },
    filteredMetrics() {
      return this.filterMetrics(this.metricsWithInfo);
    },
  },
  watch: { /*
    Watches an existing property
    Only runs when the watched property changes */
  },
  methods: {
    url(section) {
      return {
        name: 'institutional-qnr-results-section',
        params: { section },
      };
    },
    findSection(name) {
      if (!this.sections) return null;
      return this.sections.find(s => s.name === name);
    },
    findQuestion(section, slug) {
      if (!section || !slug) return null;
      if (typeof section === 'string') section = this.findSection(section);

      if (!section) return null;
      return section.questions.find(q => q.slug === slug);
    },
    getResponses(section, slug) {
      const question = this.findQuestion(section, slug);
      if (!question) return null;
      return question.responses;
    },
    async getClientGroups() {
      const res = await (await fetch('/api/v1/client-groups')).json();
      this.clientGroups = res.client_groups;
    },
    async loadMetrics() {
      const res = await getMetrics();

      const hash = {};
      res.data.metrics.forEach(m => hash[m.id] = m);
      this.metricHash = hash;
    },
    filterMetrics() {
      const filtered = this.metricsWithInfo.filter(m => m.pct >= 0.5);
      if (filtered.length >= 3) return filtered;

      return this.metricsWithInfo.slice(0, 3);
    },
  },
};
</script>


<style scoped>

</style>
